import Immutable from 'immutable';
import { createSelector } from '@reduxjs/toolkit';

import { getMemberWorkspaceInfoList } from '../account';
import { selectAllVideoIds, selectVideoById } from '../dashboard';
import { getTemplateById } from '../templates';

import {
    type Blueprint,
    type BlueprintDisplayGroup,
    type DashboardVideo,
    type NarrativeThreadEnum,
    RoleEnum,
} from 'js/src/client/types.gen';
import type { DropdownItemProp } from 'js/src/components/common/ui/Dropdown';
import { DROPDOWN_DIVIDER } from 'js/src/components/common/ui/Dropdown';
import { DROPDOWN_ACTIONS } from 'js/src/libs/blueprints-consts';
import { ImmutableFromJS, ImmutableToJS } from 'js/src/libs/consts';
import type { RootState } from 'js/src/libs/RootState';

/**
 * Get any blueprint by id (custom or public).
 */
export function getBlueprint(state: RootState, blueprintId: number | null): Blueprint | null {
    if (blueprintId === null) {
        return null;
    }
    const matchingBlueprint = state.blueprints.blueprintList.find(
        (blueprint) => blueprint.id === blueprintId,
    );
    if (matchingBlueprint) {
        return matchingBlueprint;
    }
    return null;
}

/**
 * Get a custom blueprint by id.
 */
export function getCustomBlueprint(state: RootState, blueprintId: number | null): Blueprint | null {
    if (blueprintId === null) {
        return null;
    }
    const matchingBlueprint = state.blueprints.blueprintList.find(
        (blueprint) => blueprint.id === blueprintId,
    );
    if (matchingBlueprint && !matchingBlueprint.is_public) {
        return matchingBlueprint;
    }
    return null;
}

export const getPublicBlueprintsList = createSelector(
    (state: RootState) => state.blueprints.blueprintList,
    (blueprints) => blueprints.filter((blueprint) => blueprint.is_public),
);

export const getCustomBlueprintsList = createSelector(
    (state: RootState) => state.blueprints?.blueprintList,
    (blueprints) => (blueprints ?? []).filter((blueprint) => !blueprint.is_public),
);

export const getBlueprintIds = createSelector(getCustomBlueprintsList, (blueprints) =>
    Immutable.List(blueprints.map((blueprint) => blueprint.id)),
);

export const getBlueprintIdsByNarrativeThread = createSelector(
    getCustomBlueprintsList,
    (_: RootState, narrativeThread: NarrativeThreadEnum | null) => narrativeThread,
    (blueprints, narrativeThread) => {
        if (narrativeThread === null) {
            return Immutable.List(blueprints.map((blueprint) => blueprint.id));
        }
        return Immutable.List(
            blueprints
                .filter((blueprint) => blueprint.narrative_thread === narrativeThread)
                .map((blueprint) => blueprint.id),
        );
    },
);

/**
 * Check if blueprints are currently loading
 */
export function isBlueprintsLoading(state: RootState): boolean {
    return state.blueprints.isLoading;
}

/**
 * Get the main video ID for a blueprint
 */
export function getBlueprintVideoId(state: RootState, blueprintId: number): number | null {
    const blueprint = getBlueprint(state, blueprintId);
    return blueprint?.video_ids?.[0] || null;
}

/*
 * Create dropdown menu options for blueprint tiles
 */
export const selectDropdownItemProps = createSelector(
    (state: RootState) => getMemberWorkspaceInfoList(state),
    (
        workspaces,
    ): Immutable.List<DropdownItemProp<{ type: DROPDOWN_ACTIONS; workspaceId?: number }>> => {
        // Add delete and edit template option
        let dropdownItemPropsList: Immutable.List<
            DropdownItemProp<{ type: DROPDOWN_ACTIONS; workspaceId?: number }>
        > = ImmutableFromJS([
            {
                id: DROPDOWN_ACTIONS.CREATE,
                text: 'Create new video',
                icon: 'add',
                onSelectData: { type: DROPDOWN_ACTIONS.CREATE },
            },
            {
                id: DROPDOWN_ACTIONS.EDIT_BASE_VIDEO,
                text: 'Edit base video',
                icon: 'edit',
                onSelectData: { type: DROPDOWN_ACTIONS.EDIT_BASE_VIDEO },
            },
            {
                id: DROPDOWN_ACTIONS.EDIT_INSTRUCTIONS,
                text: 'Edit instructions',
                icon: 'description',
                onSelectData: { type: DROPDOWN_ACTIONS.EDIT_INSTRUCTIONS },
            },
        ]);

        if (SERVER.USER.role === RoleEnum.ADMIN) {
            // add in the "Copy to -> workspace" options, if there are accessible workspaces
            const workspaceOptions: Immutable.List<
                DropdownItemProp<{ type: DROPDOWN_ACTIONS; workspaceId?: number }>
            > = workspaces.map((workspace) => {
                return ImmutableFromJS({
                    id: `workspace-${workspace.get('id')}`,
                    text: workspace.get('name'),
                    onSelectData: {
                        type: DROPDOWN_ACTIONS.COPY_TO,
                        workspaceId: workspace.get('id'),
                    },
                });
            });
            if (workspaceOptions.size > 0) {
                let finalWorkspaceOptions = workspaceOptions;
                if (workspaceOptions.size > 1) {
                    finalWorkspaceOptions = workspaceOptions
                        .push(
                            ImmutableFromJS({
                                id: DROPDOWN_DIVIDER,
                            }),
                        )
                        .push(
                            ImmutableFromJS({
                                id: 'copy-to-multiple',
                                text: 'Multiple workspaces...',
                                onSelectData: {
                                    type: DROPDOWN_ACTIONS.COPY_TO_MULTIPLE,
                                },
                            }),
                        );
                }

                let nestedDropdown = ImmutableFromJS({
                    id: 'copy-to',
                    nestedDropdown: {
                        toggleProps: Immutable.fromJS({
                            text: 'Copy to',
                            icon: 'content_copy',
                        }),
                        limitMenuHeight: true,
                    },
                }) as DropdownItemProp<{
                    type: DROPDOWN_ACTIONS;
                    workspaceId?: number;
                }>;
                nestedDropdown = nestedDropdown.setIn(
                    ['nestedDropdown', 'itemPropsList'],
                    finalWorkspaceOptions,
                );
                dropdownItemPropsList = dropdownItemPropsList.push(nestedDropdown);
            }
        }

        dropdownItemPropsList = dropdownItemPropsList.push(
            ImmutableFromJS({
                id: DROPDOWN_ACTIONS.DELETE,
                'data-testid': 'delete-blueprint',
                text: 'Delete',
                icon: 'delete',
                onSelectData: { type: DROPDOWN_ACTIONS.DELETE },
            }) as DropdownItemProp<{
                type: DROPDOWN_ACTIONS;
                workspaceId?: number;
            }>,
        );

        return dropdownItemPropsList;
    },
);

/**
 * Returns a list of public blueprints that are supported by the given template ID and narrative thread.
 */
export const getSupportedPublicBlueprints = createSelector(
    [
        getPublicBlueprintsList,
        (_: RootState, narrativeThread: NarrativeThreadEnum) => narrativeThread,
        (_: RootState, _thread: string, templateId: string) => templateId,
    ],
    (blueprints, narrativeThread, templateId) =>
        blueprints
            .filter((b) => b.narrative_thread === narrativeThread)
            .filter((b) => b.supported_templates.includes(templateId)),
);

// any video past script composer stage can used as the basis for a blueprint
export const selectBlueprintCandidateVideos = createSelector(
    [(state: RootState) => selectAllVideoIds(state), (state: RootState) => state],
    (videoIds, state) =>
        videoIds.reduce<DashboardVideo[]>((acc, videoId) => {
            const video = selectVideoById(state, videoId);
            if (video && video.get('is_in_script_composer') === false) {
                acc.push(ImmutableToJS(video));
            }
            return acc;
        }, []),
);

export type BlueprintDisplayGroupWithBlueprints = {
    displayGroup: BlueprintDisplayGroup;
    blueprints: Blueprint[];
};

export const getPublicBlueprintsGroupedByDisplayGroup = createSelector(
    getPublicBlueprintsList,
    (blueprints): BlueprintDisplayGroupWithBlueprints[] => {
        const groupMap = new Map<number, BlueprintDisplayGroupWithBlueprints>();

        for (const blueprint of blueprints) {
            if (!blueprint.display_group?.id) {
                continue;
            }
            const groupId = blueprint.display_group.id;
            if (!groupMap.has(groupId)) {
                groupMap.set(groupId, {
                    displayGroup: blueprint.display_group,
                    blueprints: [],
                });
            }
            groupMap.get(groupId)!.blueprints.push(blueprint);
        }

        const groups = Array.from(groupMap.values());
        for (const group of groups) {
            group.blueprints.sort((a, b) => {
                if (a.display_order == null && b.display_order == null) return 0;
                if (a.display_order == null) return 1;
                if (b.display_order == null) return -1;
                return a.display_order - b.display_order;
            });
        }
        return groups.sort((a, b) => a.displayGroup.order - b.displayGroup.order);
    },
);

export const selectTemplateIdToDisplayNameMap = createSelector(
    [selectBlueprintCandidateVideos, (state: RootState) => state],
    (videos, state) =>
        videos.reduce<Record<string, string>>((acc, video) => {
            const templateId = video.template_id;
            if (!acc[templateId]) {
                const displayName = getTemplateById(state, templateId)?.get('display_name');
                if (displayName) {
                    acc[templateId] = displayName;
                }
            }
            return acc;
        }, {}),
);
